import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import colors from '../../assets/theme/colors';
import LogFormShell from '../../components/UI/LogFormShell';

const text = (value = '') => String(value || '').trim();

const buildDraft = (candidate) => {
  const selectedCandidate = candidate || {};
  const payload = selectedCandidate.payload || {};
  return {
    id: text(payload.id || selectedCandidate.key),
    title: text(payload.title || selectedCandidate.label),
    description: text(payload.description),
    successCriteria: text(payload.successCriteria),
    supportStrategy: text(payload.supportStrategy),
    category: text(payload.category),
    source: text(payload.source || selectedCandidate.origin?.collection),
    status: text(payload.status || 'active') || 'active',
    startDate: text(payload.startDate),
    targetDate: text(payload.targetDate),
  };
};

const GoalCandidateReviewSheet = ({ open, candidate, saving = false, onClose, onSave }) => {
  const [draft, setDraft] = React.useState(() => buildDraft(candidate));
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setDraft(buildDraft(candidate));
      setError('');
    }
  }, [candidate, open]);

  const update = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const save = () => {
    if (!text(draft.title)) {
      setError('Add a goal before saving.');
      return;
    }
    onSave?.({ ...draft, title: text(draft.title) });
  };

  return (
    <LogFormShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Review goal"
      subtitle="Check or edit this goal before adding it to the Profile."
      maxWidth="sm"
      footer={(
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={saving} onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={save}
            sx={{ borderRadius: '999px', px: 2.4, textTransform: 'none', fontWeight: 900, bgcolor: colors.brand.deep }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Add goal'}
          </Button>
        </Stack>
      )}
    >
      <Stack spacing={1.5}>
        <TextField required label="Goal" value={draft.title} onChange={update('title')} fullWidth />
        <TextField label="Description" value={draft.description} onChange={update('description')} multiline minRows={3} fullWidth />
        <TextField label="How will you know it is working?" value={draft.successCriteria} onChange={update('successCriteria')} multiline minRows={2} fullWidth />
        <TextField label="Support strategy" value={draft.supportStrategy} onChange={update('supportStrategy')} multiline minRows={2} fullWidth />
        <TextField label="Category" value={draft.category} onChange={update('category')} fullWidth />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
          <TextField label="Start date" type="date" value={draft.startDate} onChange={update('startDate')} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Target date" type="date" value={draft.targetDate} onChange={update('targetDate')} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
        {candidate?.payload?.referenceCount > 1 ? (
          <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.86rem' }}>
            {`Found in ${candidate.payload.referenceCount} records.`}
          </Typography>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>
    </LogFormShell>
  );
};

export default GoalCandidateReviewSheet;
